import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [feedback, setFeedback] = useState({
    type: 'improvement',
    title: '',
    description: '',
    priority: 'medium'
  });
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchMyFeedbacks();
    }
  }, [user]);

  const fetchMyFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/feedback/my-feedbacks`, {
        withCredentials: true
      });
      setMyFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(
        `${API}/feedback/submit`,
        {
          ...feedback,
          page_url: window.location.href,
          user_agent: navigator.userAgent
        },
        { withCredentials: true }
      );

      alert(language === 'tr' 
        ? '✅ Geri bildiriminiz gönderildi! Teşekkür ederiz.'
        : '✅ Feedback submitted! Thank you.');

      setFeedback({
        type: 'improvement',
        title: '',
        description: '',
        priority: 'medium'
      });

      fetchMyFeedbacks();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(language === 'tr' 
        ? '❌ Geri bildirim gönderilemedi!'
        : '❌ Failed to submit feedback!');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'bug': '🐛',
      'feature': '✨',
      'improvement': '🔧',
      'question': '❓',
      'other': '💭'
    };
    return icons[type] || '💬';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Geri Bildirim' : 'Feedback'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            💬 {language === 'tr' ? 'Geri Bildirim' : 'Feedback'}
          </h1>
          <p className="text-gray-600">
            {language === 'tr'
              ? 'Düşüncelerinizi, önerilerinizi ve sorunları bizimle paylaşın'
              : 'Share your thoughts, suggestions, and issues with us'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feedback Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? 'Yeni Geri Bildirim' : 'New Feedback'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'tr' ? 'Tip' : 'Type'}
                    </label>
                    <select
                      value={feedback.type}
                      onChange={(e) => setFeedback({ ...feedback, type: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="bug">🐛 {language === 'tr' ? 'Hata' : 'Bug'}</option>
                      <option value="feature">✨ {language === 'tr' ? 'Özellik' : 'Feature'}</option>
                      <option value="improvement">🔧 {language === 'tr' ? 'İyileştirme' : 'Improvement'}</option>
                      <option value="question">❓ {language === 'tr' ? 'Soru' : 'Question'}</option>
                      <option value="other">💭 {language === 'tr' ? 'Diğer' : 'Other'}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {language === 'tr' ? 'Öncelik' : 'Priority'}
                    </label>
                    <select
                      value={feedback.priority}
                      onChange={(e) => setFeedback({ ...feedback, priority: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="low">🟢 {language === 'tr' ? 'Düşük' : 'Low'}</option>
                      <option value="medium">🟡 {language === 'tr' ? 'Orta' : 'Medium'}</option>
                      <option value="high">🔴 {language === 'tr' ? 'Yüksek' : 'High'}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Başlık' : 'Title'}
                  </label>
                  <input
                    type="text"
                    value={feedback.title}
                    onChange={(e) => setFeedback({ ...feedback, title: e.target.value })}
                    placeholder={language === 'tr' ? 'Kısa ve açıklayıcı...' : 'Short and descriptive...'}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    maxLength={200}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Detaylı Açıklama' : 'Detailed Description'}
                  </label>
                  <textarea
                    value={feedback.description}
                    onChange={(e) => setFeedback({ ...feedback, description: e.target.value })}
                    rows={8}
                    placeholder={language === 'tr' 
                      ? 'Lütfen mümkün olduğunca detaylı anlatın...'
                      : 'Please describe in as much detail as possible...'}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    maxLength={2000}
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {feedback.description.length}/2000
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 transition-all duration-300"
                >
                  {submitting 
                    ? (language === 'tr' ? '⏳ Gönderiliyor...' : '⏳ Submitting...') 
                    : (language === 'tr' ? '📤 Gönder' : '📤 Submit')}
                </button>
              </form>
            </div>
          </div>

          {/* My Feedbacks */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {language === 'tr' ? 'Geri Bildirimlerim' : 'My Feedbacks'}
              </h3>

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
                </div>
              ) : myFeedbacks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {language === 'tr' ? 'Henüz geri bildirim yok' : 'No feedbacks yet'}
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {myFeedbacks.map((fb) => (
                    <div key={fb.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">{getTypeIcon(fb.type)}</span>
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusColor(fb.status)}`}>
                            {fb.status}
                          </span>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-1">
                        {fb.title}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {fb.description}
                      </p>
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(fb.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
